import React, { useEffect, useState, useRef } from 'react'

//Locator-specific imports.
import { getLocationInfoFromZip } from 'services/locator'
import LocationListItem from 'steps/locator/LocationListItem'
import Map from 'steps/locator/Map'
import './Locator.scss'
import LocationMapMarker from 'steps/locator/LocationMapMarker'
import LocatorForm from 'steps/locator/LocatorForm'
import LocationCountBlurb from 'steps/locator/LocationCountBlurb'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { randomuuidv4 } from 'util/maths'
import ArrowButton from 'steps/ArrowButton3'
import SkipButton from 'steps/SkipButton'

//Misc util imports
import { stepStyle } from 'util/misc'
import analytics from 'global/AnalyticsData'

//Specific to step slides/step path
import Slider from 'react-slick'
import StartSlide from 'steps/StartSlide'

import {
  start as serviceStart,
  complete as serviceComplete,
  awardPoints,
} from 'services/locator'

const LocatorStep = ({
  step,
  breakId,
  onStepStart,
  onStepComplete,
  onGoToNext,
  onStepSkip,
  unlockNextStep,
  scrollComplete,
  isComplete,
  isEmbed,
  onAuthRequired,
  onUnhandledError,
  handleAwardPoints,
  HtmlFooter,
}) => {
  const searchRadius = 25

  const client = step.client || undefined //Sponsored client ID.

  const [locationInfo, setLocationInfo] = useState([])
  const [appMessage, setAppMessage] = useState('') //an error message.
  const [formTouched, setFormTouched] = useState(false) //If form has been modified by user.
  const [stepCompleted, setStepCompleted] = useState(false) //Step progress state
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationHover, setLocationHover] = useState(null)
  const [stepSkipped, setStepSkipped] = useState(false)
  const [isArrowVisible, setIsArrowVisible] = useState(false)

  const sliderRef = useRef(null)
  const nonceRef = useRef(null)
  const selfRef = useRef(null)
  const sentStartRef = useRef(false)

  const sliderSettings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    adaptiveHeight: true,
    slidesToScroll: 1,
  }

  const [inView, entry] = useIntersectionObserver(selfRef, {
    threshold: 0.4,
  })

  const assignPoints = async () => {
    try {
      if (nonceRef.current && stepCompleted === false) {
        const response = await awardPoints(breakId, step.id, nonceRef.current)
        const { totalPoints } = response.detail
        if (totalPoints > 0) {
          console.log(`awarded points: ${response.detail.awardedPoints}`)
          handleAwardPoints(totalPoints)
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleStartClick = async () => {
    setIsArrowVisible(true)
    try {
      const response = await serviceStart(breakId, step.id)
      nonceRef.current = response.detail.nonce
      if (onStepStart) {
        const { attempt, firstBreakEngagement } = response
        onStepStart(step, attempt, firstBreakEngagement)
      }
    } catch (e) {
      if (e.message === '401') {
        onAuthRequired && onAuthRequired()
      } else {
        onUnhandledError && onUnhandledError()
      }
    }
  }
  const onLocationHover = thisLocation => {
    //setLocationHover(thisLocation.id)
  }
  const handleNextClick = async () => {
    try {
      await serviceComplete(breakId, step.id, stepSkipped)
      onStepComplete && onStepComplete(step.id)
      onGoToNext && onGoToNext(step.id)
    } catch (e) {
      console.log(`Error going to next step: ${e}`)
    }
  }

  useEffect(() => {
    if (!step.hasStartScreen && !sentStartRef.current && inView) {
      handleStartClick()
      sentStartRef.current = true
    }
  }, [step.hasStartScreen, inView, handleStartClick])

  //listen for scrollComplete and fire off handleCompleteClick when toggled
  useEffect(() => {
    const cleanup = async () => {
      await serviceComplete(breakId, step.id, true)
    }
    if (scrollComplete && !isComplete) {
      cleanup()
    }
  }, [scrollComplete, isComplete, breakId, step.id])

  //unlock arrow if alreadyComplete
  useEffect(() => {
    if (isComplete) {
      setStepCompleted(true)
    }
  }, [isComplete])

  const onLocationClicked = locationEmitted => {
    console.log('event: step_interaction_locator_directions_lookup')
    window.dataLayer.push({
      event: 'step_interaction_locator_directions_lookup',
    })
    analytics.stepEvent('step_interaction_locator_directions_lookup', step, {
      context: locationEmitted.name,
      context_id: locationEmitted.id,
      context_client: locationEmitted.client,
    })
  }

  //Triggered when a marker selection is made with the location list children.
  const changeSelectedLocation = location => {
    if (location !== null) {
      setSelectedLocation(location.id)
      setStepCompleted(true)
      console.log('event: step_interaction_locator_marker_selection')
      window.dataLayer.push({
        event: 'step_interaction_locator_marker_selection',
      })
      analytics.stepEvent('step_interaction_locator_marker_selection', step, {
        context: location.name,
        context_id: location.id,
        context_client: location.client,
      })
    } else {
      setSelectedLocation(null)
    }
  }

  //Reformatting locations data so that google maps can read it.
  const locationsConstructor = locs => {
    let l2 = locs.map(function (location) {
      location.coordinates = {
        lat: location.gpsLocation[1],
        lng: location.gpsLocation[0],
      }
      delete location.gpsLocation
      if (!location.id) {
        location.id = randomuuidv4()
      }
      return location
    })
    return l2
  }

  //User inputs zipcode on form, we look it up and populate locations.
  const lookupZipCode = async function (zip) {
    try {
      setFormTouched(true)
      setStepCompleted(true)
      let params = {
        client: client,
        zip: zip,
        distance: searchRadius || 25,
      }
      let resp = await getLocationInfoFromZip(params)
      let locations = await locationsConstructor(resp.detail)
      setLocationInfo(locations)
      if (formTouched === false) {
        assignPoints()
      }

      console.log('event: step_interaction_locator_button_click')
      window.dataLayer.push({
        event: 'step_interaction_locator_button_click',
      })
      analytics.stepEvent('step_interaction_locator_button_click', step)
    } catch (e) {
      setAppMessage(`Error getting location information: ${e}`)
    }
  }

  const handleNextClickFromStart = () => {
    handleStartClick()
    sliderRef.current.slickNext()
  }

  const handleStepSkip = () => {
    onStepSkip(step.id)
    setStepSkipped(true)
  }

  return (
    <>
      <div
        className="sponsored-flow-panel location--panel"
        style={stepStyle(step)}
        ref={selfRef}
      >
        <Slider
          ref={slider => (sliderRef.current = slider)}
          {...sliderSettings}
          className="locator__slider"
        >
          {step.hasStartScreen && (
            <StartSlide
              headline={step.startHeader}
              para={step.startBody}
              footer={step.startFooter}
              backgroundImageId={step.startBackgroundImageId}
              backgroundColor={step.startBackgroundColor}
              onStartClicked={handleNextClickFromStart}
              stepType={step.type}
            />
          )}
          <div className="locator-step--slide">
            <section className="locator-step">
              <div className="locator--message">{appMessage}</div>
              <div className="locator">
                <div className="step-content">
                  {client ? (
                    <>
                      <div className="locator--header">
                        <div className="locator--title">
                          {
                            /* headline */ step.headline && (
                              <h2
                                className="locator-step--headline"
                                dangerouslySetInnerHTML={{
                                  __html: step.headline,
                                }}
                              ></h2>
                            )
                          }
                          {
                            /* title subhead */ step.introCopy && (
                              <h3
                                className="locator-step--subhead"
                                dangerouslySetInnerHTML={{
                                  __html: step.introCopy,
                                }}
                              ></h3>
                            )
                          }
                        </div>
                        <LocatorForm
                          onZipCodeChanged={lookupZipCode}
                          step={step}
                        />
                      </div>
                      <div className="locator--content">
                        <div className="locator--map-container">
                          <Map locations={locationInfo}>
                            {locationInfo.map(location => {
                              return (
                                <LocationMapMarker
                                  key={location.id}
                                  isSelected={location.id === selectedLocation}
                                  changeSelectedLocation={
                                    changeSelectedLocation
                                  }
                                  location={location}
                                  step={step}
                                  className={`dgsdgsdgsdg`}
                                  isHovered={location.id === locationHover}
                                >
                                  <LocationListItem
                                    key={location.id}
                                    isSelected={
                                      location.id === selectedLocation
                                    }
                                    isInSidebar={false}
                                    location={location}
                                    onLocationClicked={onLocationClicked}
                                    changeSelectedLocation={
                                      changeSelectedLocation
                                    }
                                  />
                                </LocationMapMarker>
                              )
                            })}
                          </Map>
                        </div>
                        <div className="locator--locations-list-container">
                          <div className="locator--locations-list-positioner">
                            <div className="locator--locations-list">
                              <LocationCountBlurb
                                locationInfo={locationInfo}
                                formTouched={formTouched}
                              />
                              {locationInfo.map(location => {
                                return (
                                  <LocationListItem
                                    key={location.id}
                                    isSelected={
                                      location.id === selectedLocation
                                    }
                                    isInSidebar={true}
                                    location={location}
                                    onLocationClicked={onLocationClicked}
                                    changeSelectedLocation={
                                      changeSelectedLocation
                                    }
                                    onLocationHover={onLocationHover}
                                  />
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={`error-text`}>
                      Locator step client not defined. Store locator cannot be
                      loaded.
                    </div>
                  )}
                  {stepCompleted !== true && isArrowVisible && (
                    <SkipButton onClick={handleStepSkip} step_id={step.id} />
                  )}
                  <ArrowButton
                    visible={isArrowVisible}
                    clickable={stepCompleted === true}
                    onClick={handleNextClick}
                  />
                </div>
              </div>
            </section>
          </div>
        </Slider>

        {HtmlFooter && <HtmlFooter />}
      </div>
    </>
  )
}

export default LocatorStep
